import React from 'react';
import {
  Brain,
  Clock,
  Compass,
  Ear,
  Flag,
  HandHeart,
  Handshake,
  Heart,
  Lightbulb,
  ListChecks,
  type LucideIcon,
  MessageCircleQuestion,
  MessageSquareText,
  Plus,
  Scale,
  Shield,
  Smile,
  Sparkles,
  Target,
  Users,
  Wind,
  X,
} from 'lucide-react';

import { adminContent } from 'services/api';
import type { RoleplayScorecardSuggestionRequest } from 'services/api/adminContentClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  createEmptyScorecardCategory,
  DEFAULT_ROLEPLAY_SCORECARD_ICON,
  ROLEPLAY_SCORECARD_DESCRIPTION_MAX_LENGTH,
  ROLEPLAY_SCORECARD_ICON_NAMES,
  ROLEPLAY_SCORECARD_LABEL_MAX_LENGTH,
  ROLEPLAY_SCORECARD_MAX_CATEGORIES,
  type RoleplayScorecardIconName,
  type ScorecardCategoryDraft,
  scorecardCategoriesValidationMessage,
} from './roleplayMetadata';

const SCORECARD_ICON_COMPONENTS: Record<RoleplayScorecardIconName, LucideIcon> = {
  Target,
  Heart,
  HandHeart,
  Ear,
  MessageSquareText,
  MessageCircleQuestion,
  Wind,
  Flag,
  Handshake,
  Lightbulb,
  Shield,
  Compass,
  Scale,
  Sparkles,
  Users,
  Clock,
  ListChecks,
  Smile,
  Brain,
};

const isKnownIcon = (icon: string): icon is RoleplayScorecardIconName =>
  (ROLEPLAY_SCORECARD_ICON_NAMES as readonly string[]).includes(icon);

const humanizeIconName = (icon: string) => icon.replace(/([a-z])([A-Z])/g, '$1 $2');

const IconOption: React.FC<{ icon: RoleplayScorecardIconName }> = ({ icon }) => {
  const IconComponent = SCORECARD_ICON_COMPONENTS[icon];
  return (
    <span className="flex items-center gap-2">
      <IconComponent className="h-4 w-4 shrink-0" aria-hidden="true" />
      {humanizeIconName(icon)}
    </span>
  );
};

const SUGGESTION_FALLBACK_ERROR = 'Category suggestions are unavailable right now. Please try again.';

const suggestionErrorMessage = (error: unknown): string => {
  const data = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  const detail = typeof data === 'object' && data !== null
    ? (data as { detail?: unknown }).detail
    : undefined;
  return typeof detail === 'string' && detail.trim() ? detail : SUGGESTION_FALLBACK_ERROR;
};

/**
 * Suggested rows are unsaved drafts. Reuse an existing row's key when the
 * suggestion has the same label so learner progress keeps comparing; otherwise
 * leave the key blank so the server derives it from the final label on save.
 */
const suggestionsToDrafts = (
  suggestions: unknown,
  existing: ScorecardCategoryDraft[],
): ScorecardCategoryDraft[] => {
  if (!Array.isArray(suggestions)) return [];
  const existingKeysByLabel = new Map(
    existing
      .filter((category) => category.key.trim())
      .map((category) => [category.label.trim().toLowerCase(), category.key]),
  );
  return suggestions
    .filter((item): item is Record<string, unknown> => (
      typeof item === 'object' && item !== null && !Array.isArray(item)
    ))
    .slice(0, ROLEPLAY_SCORECARD_MAX_CATEGORIES)
    .map((item) => {
      const label = typeof item.label === 'string' ? item.label.trim() : '';
      const icon = typeof item.icon === 'string' && isKnownIcon(item.icon)
        ? item.icon
        : DEFAULT_ROLEPLAY_SCORECARD_ICON;
      return {
        key: existingKeysByLabel.get(label.toLowerCase()) ?? '',
        label,
        description: typeof item.description === 'string' ? item.description.trim() : '',
        icon,
      };
    });
};

export type RoleplayScorecardEditorProps = {
  categories: ScorecardCategoryDraft[];
  onChange: (categories: ScorecardCategoryDraft[]) => void;
  /** Builds the suggestion request from the current, possibly unsaved, draft. */
  buildSuggestionRequest: () => RoleplayScorecardSuggestionRequest;
};

export const RoleplayScorecardEditor: React.FC<RoleplayScorecardEditorProps> = ({
  categories,
  onChange,
  buildSuggestionRequest,
}) => {
  const [suggesting, setSuggesting] = React.useState(false);
  const [suggestError, setSuggestError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const validationMessage = scorecardCategoriesValidationMessage(categories);
  const atLimit = categories.length >= ROLEPLAY_SCORECARD_MAX_CATEGORIES;

  const updateCategory = (index: number, patch: Partial<ScorecardCategoryDraft>) => {
    onChange(categories.map((category, currentIndex) => (
      currentIndex === index ? { ...category, ...patch } : category
    )));
  };

  const removeCategory = (index: number) => {
    onChange(categories.filter((_, currentIndex) => currentIndex !== index));
  };

  const addCategory = () => {
    if (atLimit) return;
    onChange([...categories, createEmptyScorecardCategory()]);
  };

  const suggestCategories = async () => {
    if (
      categories.length > 0
      && !window.confirm('Replace the current scorecard categories with AI suggestions?')
    ) {
      return;
    }
    setSuggesting(true);
    setSuggestError(null);
    try {
      const response = await adminContent.operations.suggestRoleplayScorecardCategories(
        buildSuggestionRequest(),
      );
      if (!mountedRef.current) return;
      const suggested = suggestionsToDrafts(response.data?.categories, categories);
      if (suggested.length === 0) {
        setSuggestError(SUGGESTION_FALLBACK_ERROR);
        return;
      }
      onChange(suggested);
    } catch (error) {
      if (!mountedRef.current) return;
      setSuggestError(suggestionErrorMessage(error));
    } finally {
      if (mountedRef.current) setSuggesting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="roleplay-scorecard-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Label className="text-sm font-medium">Scorecard categories</Label>
          <p className="text-xs text-muted-foreground">
            Optional. Learners get a 0–100 score per skill after practice. Use 2–6 categories;
            keep keys stable so progress compares across attempts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="auto"
            onClick={suggestCategories}
            disabled={suggesting}
            aria-busy={suggesting || undefined}
            data-testid="roleplay-scorecard-suggest"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {suggesting ? 'Suggesting...' : 'Suggest with AI'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="auto"
            onClick={addCategory}
            disabled={atLimit || suggesting}
            data-testid="roleplay-scorecard-add"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add category
          </Button>
        </div>
      </div>

      {suggestError ? (
        <p role="alert" className="text-xs text-destructive" data-testid="roleplay-scorecard-error">
          {suggestError}
        </p>
      ) : null}

      {categories.length === 0 ? (
        <p className="text-xs text-muted-foreground/80">No scorecard for this roleplay.</p>
      ) : (
        <div className="space-y-3">
          {categories.map((category, index) => {
            const number = index + 1;
            const labelId = `roleplay-scorecard-label-${index}`;
            const iconId = `roleplay-scorecard-icon-${index}`;
            const descriptionId = `roleplay-scorecard-description-${index}`;
            const iconKnown = isKnownIcon(category.icon);
            return (
              <div
                // Rows are fully controlled and have no client id (keys are optional until saved).
                key={index}
                className="space-y-3 rounded-lg border border-border/40 bg-white/5 p-3"
                data-testid={`roleplay-scorecard-row-${index}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    Category {number}
                    {category.key ? (
                      <span className="ml-2 font-mono normal-case text-muted-foreground/80">
                        key: {category.key}
                      </span>
                    ) : null}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove category ${number}`}
                    onClick={() => removeCategory(index)}
                    disabled={suggesting}
                    data-testid={`roleplay-scorecard-remove-${index}`}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
                  <div className="space-y-1">
                    <Label htmlFor={labelId}>Label</Label>
                    <Input
                      id={labelId}
                      value={category.label}
                      maxLength={ROLEPLAY_SCORECARD_LABEL_MAX_LENGTH}
                      onChange={(event) => updateCategory(index, { label: event.target.value })}
                      placeholder="e.g. Active listening"
                      aria-invalid={!category.label.trim() || undefined}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={iconId}>Icon</Label>
                    <Select
                      value={category.icon}
                      onValueChange={(value) => updateCategory(index, { icon: value })}
                    >
                      <SelectTrigger id={iconId} className="w-full">
                        <SelectValue placeholder={`Default (${DEFAULT_ROLEPLAY_SCORECARD_ICON})`} />
                      </SelectTrigger>
                      <SelectContent>
                        {category.icon && !iconKnown ? (
                          <SelectItem value={category.icon}>
                            Unknown icon · {category.icon}
                          </SelectItem>
                        ) : null}
                        {ROLEPLAY_SCORECARD_ICON_NAMES.map((icon) => (
                          <SelectItem key={icon} value={icon}>
                            <IconOption icon={icon} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor={descriptionId}>What good looks like</Label>
                  <Textarea
                    id={descriptionId}
                    rows={2}
                    value={category.description}
                    maxLength={ROLEPLAY_SCORECARD_DESCRIPTION_MAX_LENGTH}
                    onChange={(event) => updateCategory(index, { description: event.target.value })}
                    placeholder="Describe the behavior that earns a high score."
                    aria-invalid={!category.description.trim() || undefined}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {validationMessage ? (
        <p className="text-xs font-medium text-amber-700" data-testid="roleplay-scorecard-validation">
          {validationMessage}
        </p>
      ) : null}
    </div>
  );
};

export default RoleplayScorecardEditor;
